import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Form, Row, FloatingLabel } from 'react-bootstrap';
import styles from '../QuestionnaireManagement.module.css';
import { LeftArrowIcon } from '../../../components/icons/Icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    setFieldValue,
    setGroups,
} from './AddQuestionnaireSlice';
import { setSelectedItem } from '../../../components/PerinHealthGridSlice';
import managementStyles from '../../ManagementPages.module.css';
import ProgressBar from '../../../components/ProgressBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showAlert } from '../../../components/PerinHealthAlertSlice';

const QuestionnaireSetup = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const selectedOrgIdRef = useRef(null);
    const addQuestionnaireState = useSelector((state) => state.addQuestionnaire);
    const isAdmin = useSelector((state) => state.userInfo.isAdmin);
    const isUpdateMode = addQuestionnaireState.isUpdateMode;
    const groups = addQuestionnaireState.groups;
    const organizationsHierarchy = useSelector((state) => state.userInfo.organizationsHierarchy);
    const userGroups = useSelector((state) => state.userInfo.groups);
    const selectedItem = useSelector(
        (state) => state.perinHealthGrid.selectedItem || {}
    );
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [validated, setValidated] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        if (selectedItem && Object.keys(selectedItem).length !== 0) {
            handleOrganizationChange({ target: { value: selectedItem.organizationId } });
    
            setSelectedOrgId(selectedItem.organizationId);
            setSelectedGroupId(selectedItem.groupId); 
        } else {
            if (organizationsHierarchy.length === 1) {
                selectedOrgIdRef.current = organizationsHierarchy[0].organizationId;
                setSelectedOrgId(selectedOrgIdRef.current);
                handleOrganizationChange({ target: { value: selectedOrgIdRef.current } });
            }
        }
    }, [selectedItem, dispatch, organizationsHierarchy]);

    const getSelectedOrganization = (selectedOrgId) => {
        return organizationsHierarchy.find((org) => org.organizationId === Number(selectedOrgId));
    };

    const getSelectedGroup = (selectedOrgId, selectedGroupId) => {
        const organization = getSelectedOrganization(selectedOrgId);
        if (organization && organization.groups.length > 0) {
            return organization.groups.find((group) => group.groupId === Number(selectedGroupId));
        }
        return null;
    };

    const handleOrganizationChange = (e) => {
        const selectedOrgId = e.target.value;
        setSelectedOrgId(selectedOrgId); 
        selectedOrgIdRef.current = selectedOrgId;
        const organization = getSelectedOrganization(selectedOrgId);
        
        handleControlChange('organizationId', organization?.organizationId);
        handleControlChange('organizationName', organization?.organizationName);
    
        if (organization && organization.groups.length === 1) {
            //dispatch(setGroups([organization.groups[0]]));
            const selectedGroup = organization.groups[0];
            setSelectedGroupId(selectedGroup.groupId);
            dispatch(setGroups(organization.groups));
            handleControlChange('groupId', selectedGroup.groupId);
            handleGroupChange({ target: { value: selectedGroup.groupId } });
        } else {
            if (organization && organization.groups.length > 0) {
                const allGroups = [{ groupId: "", groupName: 'Select Group' }, ...organization.groups];
                dispatch(setGroups(allGroups));
            } else {
                dispatch(setGroups([{ groupId: "", groupName: 'No Group' }]));
                setSelectedGroupId('');
                handleControlChange('groupId', '');
            }
        }
    };
    
    const handleGroupChange = (e) => {
        const selectedGroupId = e.target.value;
        setSelectedGroupId(selectedGroupId); 
        const selectedOrgId = selectedOrgIdRef.current;
        const selectedGroup = getSelectedGroup(selectedOrgId, selectedGroupId);
    
        if (selectedGroup) {
            const { groupId, groupName, subgroups } = selectedGroup;
            const { subGroupId, teams } = subgroups ? subgroups[0] || {} : {};
            const { teamId } = teams ? teams[0] || {} : {};
    
            handleControlChange('groupId', groupId);
            handleControlChange('groupName', groupName);
            handleControlChange('subGroupId', subGroupId);
            handleControlChange('teamId', teamId);
        } else {
            handleControlChange('groupId', '');
            handleControlChange('subGroupId', '');
            handleControlChange('teamId', '');
        }
    };


    const handleControlChange = (controlName, value) => {
        dispatch(setFieldValue({ [controlName]: value }));
    };

    const onQuestionaryTypeChange = (e) => {
        const selectedQuestionaryType = e.target.value;
        handleControlChange('type', selectedQuestionaryType);
    };

    // const buttonDisabled = addQuestionnaireState.groupId === "" || addQuestionnaireState.name === "" || addQuestionnaireState.type === "";

    const onNextHandler = (event) => {
        event.preventDefault();
        if (formRef.current) {
            let formIsInvalid = formRef.current.checkValidity() === false;
            setShowErrorMessage("Group, Questionnaire Name and Type are required to proceed. ");
            setValidated(formIsInvalid); 
            if (formIsInvalid) return;
        }
        if (addQuestionnaireState.existingQuestionnaire.length > 0) {
            if (addQuestionnaireState.existingQuestionnaire.find((questionnaire) => questionnaire.name === addQuestionnaireState.name)) {
                setShowErrorMessage("Questionnaire name already exists.");
                return;
            }
        }
        setValidated(true);
        navigate('/add-questionnaire');
    };

    const getPageName = (step) => {
        if (step === 0) {
            return "Questionnaire Setup";
        } else if (step === 1) {
            return "Add Questions";
        } else if (step === 2) {
            return "Preview & Publish";
        }
    };

    return (
        <div className={managementStyles.MainCard}>
            <h3 className={managementStyles.ContentTitle}>{isUpdateMode ? 'Update' : 'Add'} Questionnaire</h3>
            <div style={{paddingBottom:"15px", paddingLeft:"20px"}}>
                <ProgressBar page={0} getPageName={getPageName} totalSteps={3}/>
            </div>
            <div className={managementStyles.HideScrollContainer}>
                <Container style={{margin:"0", padding:"0", width:"90%"}}>
                    <Row>
                        <Col md={6}>
                            <Form style={{width:"300px"}} ref={formRef} noValidate validated={validated}>
                                {isAdmin && (
                                    <Form.Group controlId="orgQuestionnaireSelect" className="mb-3" >
                                        <FloatingLabel label="Organization">
                                        <Form.Select
                                            required
                                            value={selectedOrgId}
                                            className={`${managementStyles.BorderedComponent}`}
                                            onChange={handleOrganizationChange}
                                            style={{width:"300px"}}
                                            disabled={isUpdateMode}
                                        >
                                            <option value="">Select</option>
                                            {userGroups.map((organization) => (
                                                <option key={organization.organizationId} value={organization.organizationId}>
                                                    {organization.organizationName}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            Please select an organization.
                                        </Form.Control.Feedback>
                                        </FloatingLabel>
                                    </Form.Group>
                                )}
                                <Form.Group controlId="groupQuestionnaireSelect" className="mb-3">
                                    <FloatingLabel label="Group*">
                                    <Form.Select
                                        required
                                        value={addQuestionnaireState.groupId}
                                        className={`${managementStyles.BorderedComponent}`}
                                        onChange={handleGroupChange}
                                        style={{width:"300px"}}
                                    >
                                        {groups?.map((group) => (
                                            <option key={group.groupId} value={group.groupId}>
                                                {group.groupName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        Please select a group.
                                    </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="nameQuestionnaire">
                                    <FloatingLabel 
                                        label="Questionnaire Name*"
                                        className="mb-3"
                                    >
                                        <Form.Control
                                            required
                                            value={addQuestionnaireState.name}
                                            placeholder="Questionnaire Name*"
                                            style={{width:"300px"}}
                                            className={`${managementStyles.BorderedComponent}`}
                                            onChange={(e) =>
                                                handleControlChange('name', e.target.value)
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a questionnaire name.
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="typeQuestionnaireSelect">
                                    <FloatingLabel 
                                        label="Type*"
                                        className="mb-3"
                                    >
                                    <Form.Select
                                        required
                                        value={addQuestionnaireState.type}
                                        className={`${managementStyles.BorderedComponent}`}
                                        onChange={onQuestionaryTypeChange}
                                        style={{width:"300px"}}
                                    >
                                        <option value="">Select Questionnaire Type</option>
                                        <option value="Multiple Choice">Multiple Choice</option>
                                        <option value="Yes or No">Yes or No</option>
                                        <option value="Combination">Combination</option>
                                    </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            Please select a valid questionnaire type.
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="descriptionQuestionnaire">
                                    <FloatingLabel 
                                        label="Description"
                                        className="mb-3"
                                    >
                                    <Form.Control
                                        value={addQuestionnaireState.description}
                                        as="textarea"
                                        rows={2}
                                        style={{width:"300px"}}
                                        className={managementStyles.BorderedComponentTextArea}
                                        placeholder='Description'
                                        onChange={(e) =>
                                            handleControlChange('description', e.target.value)
                                        }
                                    />
                                    </FloatingLabel>
                                </Form.Group>
                               
                            </Form>
                        </Col>
                    </Row>
                </Container>
            </div>
            <div className={managementStyles.ButtonsFooterContainerWithError}>
                {showErrorMessage && (
                    <div className="page-error">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                        {showErrorMessage}
                    </div>
                )}
                <Row>
                    <Col>
                        <Button variant="primary" className={managementStyles.CreateButton} onClick={onNextHandler}>
                            Next
                        </Button>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default QuestionnaireSetup;
